import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import FeedbackSection from 'components/AssessmentEndScreen/FeedbackSection';
import ThankYou from 'components/AssessmentEndScreen/ThankYou';
import { NextStepScreen, NEXT_STEP_PHASE } from 'components/AssessmentEndScreen/NextStep';
import { useIsMobileDevice, useAssessmentNextStep, NEXT_STEP_CHECKING, NEXT_STEP_DELAYED, NEXT_STEP_UNRESOLVED } from 'hooks';
import { NEXT_STEP_STATUS } from 'services/apis/assessmentNextStep';
import { storage } from 'services/config/storage';
import { disableFullScreen } from 'utils/helper';
import React from 'react';
import { MailOutlined } from '@ant-design/icons';
import { DISQUALIFY_REASONS } from 'constants/globalConstants';
import { useTranslation } from 'react-i18next';  // Import useTranslation
import './style.scss';
import { useGameplayConfig } from 'store';
import analytics from 'services/config/analytics';

const AssessmentEndScreen = (props) => {
  const { t } = useTranslation();  // Initialize translation hook
  const showGameplayOnly = storage.get.showGameplayOnly();
  const feedbackAllowed =
    !showGameplayOnly || storage.get.feedbackAllowed();

  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false)
  const [feedbackHidden, setFeedbackHidden] = useState(false)
  // Cleared for the interview but chose "I'll do it later": the only case where an
  // automated next step still gets the feedback form.
  const [interviewDeferred, setInterviewDeferred] = useState(false)
  const { state } = useLocation()
  const isMobile = useIsMobileDevice()
  const { userId, linkId } = useParams()
  const { isFeedbackVisible, isFullScreen, isLoading: isFetchingConfig, configLanguageSettings: { layoutDirection } } = useGameplayConfig()

  // Decided once on arrival, before anything below wipes the session. SDK, embedded and
  // disqualified candidates keep today's end screen.
  const [nextStepEnabled] = useState(() => !showGameplayOnly
    && window.self === window.top
    && !(state?.disqualifyReason?.length > 0)
    && Boolean(storage.get.authToken()))
  const nextStep = useAssessmentNextStep({ linkId, enabled: nextStepEnabled })
  // The next-step check needs the login token until it settles.
  const nextStepSettled = interviewDeferred || [NEXT_STEP_STATUS.NONE, NEXT_STEP_UNRESOLVED].includes(nextStep.status)
  // Any answer other than an immediate `none` means this assessment is set up to lead into the
  // AI interview. Remembered so a later `none` ("no next round") keeps the automated screens.
  const nextStepActive = [NEXT_STEP_STATUS.PENDING, NEXT_STEP_DELAYED, NEXT_STEP_UNRESOLVED, NEXT_STEP_STATUS.READY].includes(nextStep.status)
  const [nextStepSeen, setNextStepSeen] = useState(false)
  const nextStepInPlay = nextStepSeen || nextStepActive
  // The automated flow skips feedback, unless the candidate puts the interview off.
  const feedbackShown = isFeedbackVisible && (!nextStepInPlay || interviewDeferred)

  /**
   * Track reaching the end screen — either a normal submit or a disqualification.
   */
  useEffect(() => {
    if (state?.disqualifyReason?.length > 0) {
      analytics.track('disqualified', { reason: state.disqualifyReason });
    } else {
      analytics.track('assessment_submitted');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Track a slow next-step result: once when the candidate is told they can leave, and once
   * if polling gives up without an answer.
   */
  useEffect(() => {
    if (nextStep.status === NEXT_STEP_DELAYED) analytics.track('next_step_delayed');
    if (nextStep.status === NEXT_STEP_UNRESOLVED) analytics.track('next_step_unresolved');
  }, [nextStep.status]);

  useEffect(() => {
    if (nextStepActive) setNextStepSeen(true);
  }, [nextStepActive]);

  /**
   * Remove feedback components from the DOM after translate animation
   */
  useEffect(() => {
    if (feedbackSubmitted) {
      // Only count a real user submission, not the auto-skip when feedback is
      // disallowed (that path sets feedbackHidden immediately).
      if (feedbackAllowed) {
        analytics.track('feedback_submitted')
      }
      setTimeout(() => {
        setFeedbackHidden(true)
      }, 450)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedbackSubmitted])

  /**
   * If feedback is not shown, then remove all LS
   */
  useEffect(() => {
    if (!feedbackShown) {
      if (nextStepSettled) storage.destroy.all();
    } else {
      const isFeedbackSubmittedLS = storage.get.isFeedbackSubmitted(userId)
      if (isFeedbackSubmittedLS) {
        setFeedbackSubmitted(true)
      }
    }
    if (isFullScreen) {
      disableFullScreen()
    }
  }, [feedbackShown, isFetchingConfig, isFullScreen, userId, nextStepSettled])

  /**
   * Feedback submitted while the next-step check still needed the session: wipe it once
   * the check settles, as FeedbackSection would have on submit.
   */
  useEffect(() => {
    if (nextStepEnabled && nextStepSettled && feedbackSubmitted && feedbackShown) {
      storage.destroy.all()
      storage.set.isFeedbackSubmitted(userId)
    }
  }, [nextStepEnabled, nextStepSettled, feedbackSubmitted, feedbackShown, userId])

  /**
   * If user is disqualified, only then remove LS keys except ones related to feedback and disqualification
   */
  useEffect(() => {
    if (state?.disqualifyReason?.length > 0) {
      storage.set.userDisqualified()
      storage.destroy.allExceptFeedbackSubmittedAndUserDisqualified(userId)
    }
  }, [state])

  useEffect(() => {
    if (!feedbackAllowed) {
      setFeedbackSubmitted(true)
      setFeedbackHidden(true)
    }
  }, [feedbackAllowed])

  // Slow, failed, gave up, or "no next round" after the check had started: one closing screen.
  const nextStepPhase = {
    [NEXT_STEP_STATUS.PENDING]: NEXT_STEP_PHASE.CHECKING,
    [NEXT_STEP_DELAYED]: NEXT_STEP_PHASE.SUBMITTED,
    [NEXT_STEP_UNRESOLVED]: NEXT_STEP_PHASE.SUBMITTED,
    [NEXT_STEP_STATUS.NONE]: nextStepSeen ? NEXT_STEP_PHASE.SUBMITTED : undefined,
    [NEXT_STEP_STATUS.READY]: NEXT_STEP_PHASE.READY,
  }[nextStep.status]

  // Wait for the first answer before showing anything, so the feedback page does not switch
  // its wording once it learns whether a next step is in play.
  if (isFetchingConfig || nextStep.status === NEXT_STEP_CHECKING) {
    return (
      <section className="overflowY-hidden overflowX-hidden h-full-page bg-color-primary flex flex-col justify-center items-center">
        <span className="loader"></span>
      </section>
    );
  }

  // Deferring with feedback on falls through to the feedback page below.
  if (nextStepPhase && !(interviewDeferred && isFeedbackVisible)) {
    return (
      <section className="end-screen-pg h-full-page bg-color-primary flex items-center justify-center color-primary" dir={layoutDirection}>
        <NextStepScreen
          phase={interviewDeferred ? NEXT_STEP_PHASE.LATER : nextStepPhase}
          interviewUrl={nextStep.interviewUrl}
          onLeave={() => {
            analytics.track('continued_to_interview')
            storage.destroy.all()
          }}
          onLater={() => {
            analytics.track('interview_deferred')
            setInterviewDeferred(true)
          }}
        />
      </section>
    );
  }

  return (
    <section className={`end-screen-pg h-full-page bg-color-primary flex ${isFeedbackVisible ? "feedback-top-align" : "items-center"} justify-center color-primary`} dir={layoutDirection}>
      {feedbackSubmitted && feedbackHidden ? (
        <ThankYou
          message={
            showGameplayOnly ? t('end_screen.gameplay_only_message') : null
          }
        />
      ) : (
        <></>
      )}
      {
        !feedbackHidden ? (
          <div className={`message-container ${feedbackSubmitted ? "feedback-translate" : ""} flex flex-col ${isFeedbackVisible ? "items-start" : "items-center"} justify-center`}>
            <span className='heading-emoji'>
              {state?.disqualifyReason?.length > 0 ? "⚠️" : "👏"}
            </span>
            <span className={`heading mb-18 ${!isFeedbackVisible ? "text-wrap" : ""}`}>
              {state?.disqualifyReason?.length > 0
                ? t('end_screen.disqualified')
                : t('end_screen.congratulations')}
            </span>
            {interviewDeferred ? (
              <div className="interview-later-note">
                <MailOutlined className="interview-later-note-icon" aria-hidden="true" />
                <span>{t('end_screen.next_step_later_body')}</span>
              </div>
            ) : <></>}
            <span className={`sub-heading mb-5 font-medium ${isMobile ? "text-center" : isFeedbackVisible ? "align-left" : "align-center"}`}>
              {state?.disqualifyReason?.length > 0
                ? t('end_screen.disqualified_reason', { reason: state?.disqualifyReason === DISQUALIFY_REASONS.FULLSCREEN ? t('end_screen.fullscreen') : t('end_screen.switch_tabs') })
                : isFeedbackVisible
                  ? interviewDeferred
                    ? t('end_screen.feedback_prompt')
                    : t('end_screen.success_message', { feedbackMessage: t('end_screen.feedback_prompt') })
                  : t("end_screen.congratulations_message")
              }
            </span>
          </div>
        ) : <></>
      }
      {
        isFeedbackVisible && !feedbackHidden
          ? (
            <FeedbackSection
              feedbackSubmitted={feedbackSubmitted}
              setFeedbackSubmitted={setFeedbackSubmitted}
              keepSession={!nextStepSettled}
            />
          )
          : <></>
      }
    </section>
  );
};

export default AssessmentEndScreen;
